(()=>{
const DELIVERY_CONFIG={minBusinessDays:5,maxBusinessDays:11,weekendDays:[0,6],excludedDates:[],cutoffEnabled:false,cutoffTime:'15:00',merchantTimeZone:'-06:00',holidayCalendar:'none'};
const HOLIDAY_RULES={
  us_federal:['new_year','mlk','presidents','memorial','juneteenth','independence','labor','columbus','veterans','thanksgiving','christmas'],
  us_carrier_core:['new_year','memorial','independence','labor','thanksgiving','christmas'],
};
const HOLIDAY_CALENDARS=['none',...Object.keys(HOLIDAY_RULES)];
function calendarDay(year, month, day) {
  const value = new Date(0);
  value.setUTCFullYear(year, month, day);
  value.setUTCHours(12, 0, 0, 0);
  return value;
}

function isoDay(date) {
  const year = date.getUTCFullYear();
  if (year < 1 || year > 9999) throw new RangeError('Delivery dates must be within years 0001–9999.');
  return `${String(year).padStart(4, '0')}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function nthWeekday(year, month, weekday, n) {
  const date = calendarDay(year, month, 1);
  date.setUTCDate(1 + (weekday - date.getUTCDay() + 7) % 7 + (n - 1) * 7);
  return date;
}

function lastWeekday(year, month, weekday) {
  const date = calendarDay(year, month + 1, 0);
  date.setUTCDate(date.getUTCDate() - (date.getUTCDay() - weekday + 7) % 7);
  return date;
}

function observed(year, month, day) {
  const date = calendarDay(year, month, day);
  if (date.getUTCDay() === 6) date.setUTCDate(day - 1);
  else if (date.getUTCDay() === 0) date.setUTCDate(day + 1);
  return date;
}

function holidayRules(year) {
  return {
    new_year: observed(year, 0, 1), mlk: nthWeekday(year, 0, 1, 3), presidents: nthWeekday(year, 1, 1, 3),
    memorial: lastWeekday(year, 4, 1), juneteenth: observed(year, 5, 19), independence: observed(year, 6, 4),
    labor: nthWeekday(year, 8, 1, 1), columbus: nthWeekday(year, 9, 1, 2), veterans: observed(year, 10, 11),
    thanksgiving: nthWeekday(year, 10, 4, 4), christmas: observed(year, 11, 25),
  };
}

const holidayCache = new Map();
function usHolidayDates(year, calendar) {
  if (!HOLIDAY_CALENDARS.includes(calendar)) throw new RangeError(`Holiday calendar must be ${HOLIDAY_CALENDARS.join(', ')}.`);
  const key = `${calendar}:${year}`;
  if (!holidayCache.has(key)) {
    const dates = new Set();
    for (const source of calendar === 'none' ? [] : [year, year + 1]) {
      const rules = holidayRules(source);
      for (const rule of HOLIDAY_RULES[calendar]) if (rules[rule].getUTCFullYear() === year) dates.add(isoDay(rules[rule]));
    }
    holidayCache.set(key, dates);
  }
  return holidayCache.get(key);
}

function fixedOffsetMinutes(timeZone) {
  const match = /^([+-])(\d{2}):(\d{2})$/.exec(timeZone);
  if (!match) return null;
  const hours = Number(match[2]), minutes = Number(match[3]);
  if (hours > 14 || minutes > 59 || (hours === 14 && minutes !== 0)) {
    throw new RangeError('Merchant UTC offset must be between -14:00 and +14:00.');
  }
  return (hours * 60 + minutes) * (match[1] === '-' ? -1 : 1);
}

function merchantCalendarParts(now, timeZone) {
  const offset = fixedOffsetMinutes(timeZone);
  if (offset !== null) {
    const local = new Date(now.getTime() + offset * 60000);
    return {year:local.getUTCFullYear(),month:local.getUTCMonth()+1,day:local.getUTCDate(),hour:local.getUTCHours(),minute:local.getUTCMinutes()};
  }
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone, year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', hourCycle:'h23',
  }).formatToParts(now);
  return Object.fromEntries(parts.filter(part => ['year','month','day','hour','minute'].includes(part.type)).map(part => [part.type,Number(part.value)]));
}

function validatedConfig(config) {
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    throw new TypeError('Delivery configuration must be an object.');
  }
  const result = { ...DELIVERY_CONFIG, ...config };
  const { minBusinessDays, maxBusinessDays, weekendDays, excludedDates } = result;
  if (!Number.isSafeInteger(minBusinessDays) || !Number.isSafeInteger(maxBusinessDays)
    || minBusinessDays < 1 || maxBusinessDays < minBusinessDays) {
    throw new RangeError('Delivery business days must be positive integers with minimum <= maximum.');
  }
  if (!Array.isArray(weekendDays) || weekendDays.some(day => !Number.isInteger(day) || day < 0 || day > 6)
    || new Set(weekendDays).size === 7) {
    throw new RangeError('Weekend days must use weekday numbers 0–6 and leave at least one working day.');
  }
  if (!Array.isArray(excludedDates)) throw new TypeError('Excluded dates must be an array of ISO calendar dates.');
  for (const date of excludedDates) {
    if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      throw new RangeError('Excluded dates must use YYYY-MM-DD.');
    }
    const [year, month, day] = date.split('-').map(Number);
    if (year < 1 || year > 9999 || isoDay(calendarDay(year, month - 1, day)) !== date) {
      throw new RangeError(`Invalid excluded date: ${date}`);
    }
  }
  if (typeof result.cutoffEnabled !== 'boolean') throw new TypeError('Cutoff enabled must be true or false.');
  if (result.cutoffEnabled) {
    if (typeof result.cutoffTime !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(result.cutoffTime)) {
      throw new RangeError('Order cutoff must use 24-hour HH:MM, for example 15:00.');
    }
    if (typeof result.merchantTimeZone !== 'string' || !result.merchantTimeZone.trim()) {
      throw new RangeError('Choose a merchant UTC offset or IANA time zone.');
    }
    result.merchantTimeZone = result.merchantTimeZone.trim();
    if (fixedOffsetMinutes(result.merchantTimeZone) === null) {
      new Intl.DateTimeFormat('en-US', {timeZone:result.merchantTimeZone});
    }
  }
  if (!HOLIDAY_CALENDARS.includes(result.holidayCalendar)) {
    throw new RangeError(`Holiday calendar must be ${HOLIDAY_CALENDARS.join(', ')}.`);
  }
  return { ...result, weekendDays: new Set(weekendDays), excludedDates: new Set(excludedDates) };
}

/**
 * Without cutoff, preserve the visitor-local calendar calculation.
 * With cutoff, use the merchant day; at/after cutoff or on a closed day,
 * processing starts on the next open day. Add the total business-day range once
 * from the following day. Fixed offsets never acquire daylight-saving rules.
 */
function getDeliveryEstimate(now = new Date(), config = DELIVERY_CONFIG) {
  if (!(now instanceof Date) || !Number.isFinite(now.getTime())) {
    throw new TypeError('The order date must be a valid Date.');
  }
  const { minBusinessDays, maxBusinessDays, weekendDays, excludedDates, cutoffEnabled, cutoffTime, merchantTimeZone, holidayCalendar } = validatedConfig(config);
  const local = cutoffEnabled ? merchantCalendarParts(now, merchantTimeZone) : {year:now.getFullYear(),month:now.getMonth()+1,day:now.getDate()};
  const cursor = calendarDay(local.year, local.month-1, local.day);
  const orderDateISO = isoDay(cursor);
  const closed = day => weekendDays.has(day.getUTCDay()) || excludedDates.has(isoDay(day)) || usHolidayDates(day.getUTCFullYear(), holidayCalendar).has(isoDay(day));
  if (cutoffEnabled) {
    const [hour,minute] = cutoffTime.split(':').map(Number);
    if (closed(cursor) || local.hour * 60 + local.minute >= hour * 60 + minute) {
      do {cursor.setUTCDate(cursor.getUTCDate()+1);} while (closed(cursor));
    }
  }
  const effectiveOrderDateISO = isoDay(cursor);
  let start;
  let count = 0;
  while (count < maxBusinessDays) {
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    if (closed(cursor)) continue;
    count += 1;
    if (count === minBusinessDays) start = new Date(cursor);
  }
  const end = cursor;
  const crossesYear = start.getUTCFullYear() !== end.getUTCFullYear();
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC', month: 'short', day: 'numeric', ...(crossesYear ? { year: 'numeric' } : {}),
  });
  const startLabel = formatter.format(start);
  const endLabel = formatter.format(end);
  const sameMonth = !crossesYear && start.getUTCMonth() === end.getUTCMonth();
  const label = isoDay(start) === isoDay(end)
    ? startLabel
    : `${startLabel}–${sameMonth ? end.getUTCDate() : endLabel}`;
  return {
    startISO: isoDay(start), endISO: isoDay(end), startLabel, endLabel, label, orderDateISO, effectiveOrderDateISO,
  };
}



// Explain only holidays that actually change the configured estimate.
const DELIVERY_HOLIDAY_NAMES={new_year:"New Year's Day",mlk:'Martin Luther King Jr. Day',presidents:"Presidents' Day",memorial:'Memorial Day',juneteenth:'Juneteenth',independence:'Independence Day',labor:'Labor Day',columbus:'Columbus Day',veterans:'Veterans Day',thanksgiving:'Thanksgiving',christmas:'Christmas Day'};
function getDeliveryHolidayNote(now,config,estimate){
 if(!HOLIDAY_RULES[config.holidayCalendar])return '';
 const withoutHolidays=getDeliveryEstimate(now,{...config,holidayCalendar:'none'});
 if(estimate.startISO===withoutHolidays.startISO&&estimate.endISO===withoutHolidays.endISO)return '';
 const entries=[];
 const fixedDates={new_year:[0,1],juneteenth:[5,19],independence:[6,4],veterans:[10,11],christmas:[11,25]};
 const firstYear=Number(estimate.orderDateISO.slice(0,4)),lastYear=Number(estimate.endISO.slice(0,4));
 const formatter=new Intl.DateTimeFormat('en-US',{timeZone:'UTC',month:'short',day:'numeric',...(firstYear!==lastYear?{year:'numeric'}:{})});
 for(let year=firstYear;year<=lastYear+1;year++){
  const rules=holidayRules(year);
  for(const key of HOLIDAY_RULES[config.holidayCalendar]){
   const date=rules[key],iso=isoDay(date);
   if(iso<estimate.orderDateISO||iso>estimate.endISO||config.weekendDays.includes(date.getUTCDay())||config.excludedDates.includes(iso))continue;
   const shifted=fixedDates[key]&&iso!==isoDay(calendarDay(year,...fixedDates[key]));
   entries.push({iso,label:`${DELIVERY_HOLIDAY_NAMES[key]} (${formatter.format(date)}${shifted?', observed':''})`});
  }
 }
 entries.sort((a,b)=>a.iso.localeCompare(b.iso));
 return new Intl.ListFormat('en-US',{style:'long',type:'conjunction'}).format(entries.map(entry=>entry.label));
}
function updateDeliveryHolidayNote(el,holidays){
 let note=el.nextElementSibling;
 if(!note?.matches('[data-delivery-holiday-note]')){
  if(!holidays||!el.ownerDocument)return;
  note=el.ownerDocument.createElement('small');
  note.dataset.deliveryHolidayNote='';
  note.className='cc-delivery-holiday-note';
  note.style.cssText='margin-top:4px;font-size:12px;line-height:1.45;font-weight:400;letter-spacing:normal;white-space:normal;color:inherit;max-width:100%';
  el.insertAdjacentElement('afterend',note);
 }
 note.textContent=holidays?(el.dataset.deliveryHolidayNoteTemplate||'Delivery dates account for [holidays].').replace('[holidays]',holidays):'';
 note.hidden=!holidays;
 note.style.display=holidays?'block':'none';
}

function refreshDelivery(root=document){
 const targets=[...root.querySelectorAll('[data-delivery-range]')];
 const now=new Date();
 for(const el of targets){
  if(el.dataset.deliveryFallback===undefined)el.dataset.deliveryFallback=el.textContent;
  try{
   if(el.dataset.deliveryMode==='detailed'){
    for(const phase of ['processing','transit']){
     const min=Number(el.dataset[phase+'MinDays']),max=Number(el.dataset[phase+'MaxDays']);
     if(!Number.isSafeInteger(min)||!Number.isSafeInteger(max)||min<0||max<min)throw new RangeError('Processing and transit each require non-negative whole days with minimum <= maximum.');
    }
   }
   const weekend=el.dataset.weekend;
   if(typeof weekend!=='string'||!weekend.trim())throw new RangeError('Choose the non-working days in Delivery estimates.');
   if(el.dataset.cutoffEnabled!==undefined&&!['true','false'].includes(el.dataset.cutoffEnabled))throw new RangeError('Cutoff enabled must be true or false.');
   const config={minBusinessDays:Number(el.dataset.minDays),maxBusinessDays:Number(el.dataset.maxDays),weekendDays:weekend==='none'?[]:weekend.split(',').map(Number),excludedDates:(el.dataset.excludedDates||'').split(/[\s,;]+/).filter(Boolean),cutoffEnabled:el.dataset.cutoffEnabled==='true',cutoffTime:el.dataset.cutoffTime,merchantTimeZone:el.dataset.merchantTimeZone,holidayCalendar:el.dataset.holidayCalendar??'none'};
   if(config.maxBusinessDays>365)throw new RangeError('Maximum delivery estimate is 365 business days.');
   const estimate=getDeliveryEstimate(now,config);
   el.textContent=estimate.label;
   el.dataset.deliveryStart=estimate.startISO;el.dataset.deliveryEnd=estimate.endISO;
   updateDeliveryHolidayNote(el,getDeliveryHolidayNote(now,config,estimate));
   el.removeAttribute('title');
  }catch(error){
   el.textContent=el.dataset.deliveryFallback||'';
   delete el.dataset.deliveryStart;delete el.dataset.deliveryEnd;
   updateDeliveryHolidayNote(el,'');
   if(window.Shopify?.designMode)el.title=error.message;
   else el.removeAttribute('title');
  }
 }
}
window.ccRefreshDelivery=refreshDelivery;
// Normalize direct links as well as search forms; keep non-product content out of results.
if(document.querySelector('[data-cc-product-search-redirect]')){
 const productSearchURL=new URL(window.location.href);
 productSearchURL.searchParams.set('type','product');
 productSearchURL.searchParams.delete('page');
 window.location.replace(productSearchURL.href);
}
const focusBack=new WeakMap();
function syncDialogLock(){document.documentElement.classList.toggle('cc-modal-open',Boolean(document.querySelector('dialog[open]')))}
function openDialog(id,opener){
 const dialog=document.getElementById(id);if(!dialog)return;
 document.querySelectorAll('dialog[open]').forEach(other=>{if(other!==dialog)other.close()});
 focusBack.set(dialog,opener);dialog.showModal();syncDialogLock();
 dialog.querySelector('input:not([type=hidden])')?.focus();
}
document.addEventListener('click',event=>{
 const privacyLink=event.target.closest('[data-cc-cookie-preferences]');
 if(privacyLink && typeof window.privacyBanner?.showPreferences==='function'){
  event.preventDefault();
  Promise.resolve(window.privacyBanner.showPreferences()).catch(()=>{window.location.assign(privacyLink.href)});
  return;
 }
 const open=event.target.closest('[data-cc-dialog-open]');
 if(open){openDialog(open.dataset.ccDialogOpen,open);return;}
 const close=event.target.closest('[data-cc-dialog-close]');
 if(close){close.closest('dialog')?.close();return;}
 if(event.target.matches('dialog[open]')&&event.target.id!=='cc-cart-dialog'){
  const r=event.target.getBoundingClientRect();
  if(event.clientX<r.left||event.clientX>r.right||event.clientY<r.top||event.clientY>r.bottom)event.target.close();
 }
 document.querySelectorAll('.header-help-menu[open]').forEach(menu=>{if(!menu.contains(event.target))menu.removeAttribute('open')});
});
document.addEventListener('close',event=>{syncDialogLock();if(!document.querySelector('dialog[open]'))focusBack.get(event.target)?.focus()},true);
document.addEventListener('keydown',event=>{if(event.key==='Escape')document.querySelectorAll('.header-help-menu[open]').forEach(menu=>menu.removeAttribute('open'))});
refreshDelivery();
for(const event of ['shopify:section:load','cc:content-updated','cc:product-updated','cc:cart-updated'])document.addEventListener(event,()=>refreshDelivery());
document.addEventListener('visibilitychange',()=>{if(!document.hidden)refreshDelivery()});
setInterval(()=>{if(!document.hidden)refreshDelivery()},60000);
})();
